import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { confirmPasswordReset } from "../api/auth";
import {
  BADGE_STYLES,
  BUTTON_STYLES,
  CARD_STYLES,
  INPUT_STYLES,
  TEXT_STYLES,
} from "../components/ui/CamberTheme";

export default function ResetPasswordPage() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState("");

  const resetMutation = useMutation({
    mutationFn: confirmPasswordReset,
    onSuccess: ({ data }) => {
      setFormError("");
      setMessage(data?.detail || "Password has been reset.");
      setTimeout(() => navigate("/login", { replace: true }), 1200);
    },
    onError: (err) => {
      setMessage("");
      const body = err.response?.data || {};
      const detail =
        body.new_password?.[0] ||
        body.confirm_password?.[0] ||
        body.token?.[0] ||
        body.detail ||
        "Could not reset password.";
      setFormError(detail);
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setMessage("");
    setFormError("");
    resetMutation.mutate({
      uid,
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  };

  return (
    <div
      className={`min-h-screen ${CARD_STYLES.page} flex items-center justify-center p-4`}
      style={{ fontFamily: "'Open Sans', sans-serif" }}
    >
      <form onSubmit={handleSubmit} className={`w-full max-w-md ${CARD_STYLES.surfaceBordered} p-8`}>
        <div className="text-center mb-8">
          <img src="/camber.png" alt="Camber" className="h-10 w-auto mx-auto mb-4" />
          <h1 className={`${TEXT_STYLES.title} text-[20px]`}>Create New Password</h1>
          <p className={`mt-2 text-[12px] leading-[155%] ${TEXT_STYLES.muted}`}>
            Choose a new password for your Camber account.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`block mb-2 text-[12px] ${TEXT_STYLES.label}`}>New password</label>
            <input
              className={INPUT_STYLES.base}
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          <div>
            <label className={`block mb-2 text-[12px] ${TEXT_STYLES.label}`}>Confirm password</label>
            <input
              className={INPUT_STYLES.base}
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={resetMutation.isLoading}
          className={`w-full mt-6 py-2 ${BUTTON_STYLES.primary} disabled:opacity-50`}
        >
          {resetMutation.isLoading ? "Resetting..." : "Reset Password"}
        </button>

        <Link to="/login" className="block mt-4 text-center text-[12px] leading-[155%] text-[#5937D6] hover:underline">
          Back to login
        </Link>

        {message && (
          <div className="mt-4 p-3 rounded-[4px] bg-camber-successBg border border-camber-border">
            <p className={`text-sm ${BADGE_STYLES.success}`}>{message}</p>
          </div>
        )}

        {formError && (
          <div className="mt-4 p-3 rounded-[4px] bg-camber-dangerBg border border-camber-border">
            <p className={`text-sm ${BADGE_STYLES.danger}`}>{formError}</p>
          </div>
        )}
      </form>
    </div>
  );
}
